/**
 * 🏷️ 标签管理器（环艺学姐专属 - 管理员）
 *
 * 功能：
 * - 按组分区展示标签字典（带使用次数）
 * - 新建标签 / 重命名 / 换组 / 删除标签
 * - 一键清理未使用标签
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Loader2, Pencil, Check, X, Eraser, Search, FolderPlus, ArrowUp, ArrowDown } from 'lucide-react';
import { API_BASE_URL } from '../../constants';
import { getToken } from '../../api';

const T = {
  section: 'var(--modal-input-bg)',
  inputBg: 'var(--modal-input-bg)',
  border: 'var(--border-subtle)',
  hover: 'var(--bg-hover)',
  text: 'var(--text-primary)',
  text2: 'var(--text-secondary)',
  muted: 'var(--text-muted)'
};

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  border: `1px solid ${T.border}`,
  borderRadius: '4px',
  fontSize: '13px',
  background: T.inputBg,
  color: T.text,
  boxSizing: 'border-box'
};

const TAG_GROUPS_API = `${API_BASE_URL}/api/huanshe/admin/tags/groups`;

const TagManager = ({ onMessage, onTagsChanged }) => {
  const [groups, setGroups] = useState([]);
  const [groupCatalog, setGroupCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');

  // 新建标签
  const [newName, setNewName] = useState('');
  const [newGroup, setNewGroup] = useState('其他');
  const [newGroupName, setNewGroupName] = useState('');
  const [editingGroupName, setEditingGroupName] = useState(null);
  const [editingGroupValue, setEditingGroupValue] = useState('');
  const [groupManageOpen, setGroupManageOpen] = useState(false);

  // 正在编辑的标签
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editingGroup, setEditingGroup] = useState('');

  const notify = useCallback((type, text) => onMessage?.(type, text), [onMessage]);

  const fetchTags = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/api/huanshe/admin/tags`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '获取标签失败');
      setGroups(data.groups || []);
      onTagsChanged?.();
    } catch (err) {
      console.error('Failed to fetch tags:', err);
      notify('err', err.message || '获取标签失败');
    } finally {
      setLoading(false);
    }
  }, [notify, onTagsChanged]);

  const fetchGroupCatalog = useCallback(async () => {
    try {
      const token = getToken();
      const res = await fetch(TAG_GROUPS_API, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '获取标签类型失败');
      setGroupCatalog(data.groups || []);
      onTagsChanged?.();
      setNewGroup((current) => current || data.groups?.[0]?.name || '');
    } catch (err) {
      console.error('Failed to fetch tag groups:', err);
      notify('err', err.message || '获取标签类型失败');
    }
  }, [notify, onTagsChanged]);

  useEffect(() => {
    fetchTags();
    fetchGroupCatalog();
  }, [fetchTags, fetchGroupCatalog]);

  const allGroupNames = groupCatalog.map(g => g.name);

  const createGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const token = getToken();
      const res = await fetch(TAG_GROUPS_API, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '创建标签类型失败');
      setNewGroupName('');
      setNewGroup(data.group.name);
      notify('ok', `标签类型「${name}」已创建`);
      await fetchGroupCatalog();
    } catch (err) {
      notify('err', err.message || '创建标签类型失败');
    } finally {
      setBusy(false);
    }
  };

  const saveGroup = async (group) => {
    const name = editingGroupValue.trim();
    if (!name) return;
    setBusy(true);
    try {
      const token = getToken();
      const res = await fetch(`${TAG_GROUPS_API}/${group.id}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '修改标签类型失败');
      setEditingGroupName(null);
      await Promise.all([fetchGroupCatalog(), fetchTags()]);
      notify('ok', '标签类型已更新');
    } catch (err) {
      notify('err', err.message || '修改标签类型失败');
    } finally {
      setBusy(false);
    }
  };

  const deleteGroup = async (group) => {
    const warning = group.tag_count > 0
      ? `标签类型「${group.name}」包含 ${group.tag_count} 个标签，删除后这些标签及其素材关联也会被删除。确认继续？`
      : `确认删除标签类型「${group.name}」？`;
    if (!window.confirm(warning)) return;
    setBusy(true);
    try {
      const token = getToken();
      const res = await fetch(`${TAG_GROUPS_API}/${group.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '删除标签类型失败');
      await fetchGroupCatalog();
      if (newGroup === group.name) setNewGroup('');
      notify('ok', `标签类型「${group.name}」已删除（清理 ${data.removed_tags || 0} 个标签）`);
    } catch (err) {
      notify('err', err.message || '删除标签类型失败');
    } finally {
      setBusy(false);
    }
  };

  const createTag = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/api/huanshe/admin/tags`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name, group_name: newGroup })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '创建标签失败');
      setNewName('');
      notify('ok', `标签「${name}」已创建`);
      await fetchTags();
    } catch (err) {
      notify('err', err.message || '创建标签失败');
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (tag) => {
    setEditingId(tag.id);
    setEditingName(tag.name);
    setEditingGroup(tag.group_name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
    setEditingGroup('');
  };

  const saveEdit = async () => {
    const name = editingName.trim();
    if (!name || editingId === null) return;
    setBusy(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/api/huanshe/admin/tags/${editingId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name, group_name: editingGroup })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '更新标签失败');
      cancelEdit();
      notify('ok', '标签已更新');
      await fetchTags();
    } catch (err) {
      notify('err', err.message || '更新标签失败');
    } finally {
      setBusy(false);
    }
  };

  const deleteTag = async (tag) => {
    const warn = tag.usage_count > 0
      ? `标签「${tag.name}」正在被 ${tag.usage_count} 个素材使用，删除后这些素材将失去该标签。确认删除？`
      : `确认删除标签「${tag.name}」？`;
    if (!window.confirm(warn)) return;

    setBusy(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/api/huanshe/admin/tags/${tag.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '删除标签失败');
      notify('ok', `标签「${tag.name}」已删除（从 ${data.removed_from_materials || 0} 个素材上移除）`);
      await fetchTags();
    } catch (err) {
      notify('err', err.message || '删除标签失败');
    } finally {
      setBusy(false);
    }
  };

  const moveGroup = async (group, direction) => {
    setBusy(true);
    try {
      const token = getToken();
      const res = await fetch(`${TAG_GROUPS_API}/${group.id}/order`, {
        method: 'PATCH',
        headers: {
          'Authorization': ['Bearer ', token].join(''),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ direction })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '调整标签类型顺序失败');
      await fetchGroupCatalog();
      await fetchTags();
    } catch (err) {
      notify('err', err.message || '调整标签类型顺序失败');
    } finally {
      setBusy(false);
    }
  };

  const cleanupUnused = async () => {
    if (!window.confirm('确认删除所有未被使用的标签？')) return;
    setBusy(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/api/huanshe/admin/tags/cleanup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '清理失败');
      notify('ok', `已清理 ${data.removed || 0} 个未使用标签`);
      await fetchTags();
    } catch (err) {
      notify('err', err.message || '清理失败');
    } finally {
      setBusy(false);
    }
  };

  const confirmRefresh = async () => {
    setBusy(true);
    try {
      await Promise.all([fetchGroupCatalog(), fetchTags()]);
      notify('ok', '标签类型和标签字典已确认并刷新');
    } finally {
      setBusy(false);
    }
  };

  const filteredGroups = groups
    .map(g => ({
      ...g,
      tags: (g.tags || []).filter(t =>
        !search.trim() || t.name.toLowerCase().includes(search.trim().toLowerCase())
      )
    }))
    .filter(g => g.tags.length > 0);

  const totalTags = groups.reduce((sum, g) => sum + (g.tags || []).length, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* 工具栏 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px',
        flexWrap: 'wrap'
      }}>
        <div style={{ position: 'relative', flex: '1', minWidth: '160px' }}>
          <Search
            size={13}
            style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: T.muted }}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜索标签…"
            style={{ ...inputStyle, paddingLeft: '26px' }}
          />
        </div>
        <span style={{ fontSize: '12px', color: T.muted, whiteSpace: 'nowrap' }}>
          共 {totalTags} 个标签 / {groups.length} 个分组
        </span>
        <button
          onClick={cleanupUnused}
          disabled={busy}
          style={{
            padding: '6px 10px',
            fontSize: '12px',
            borderRadius: '4px',
            border: `1px solid ${T.border}`,
            background: 'transparent',
            color: T.text2,
            cursor: busy ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <Eraser size={12} />
          清理未使用
        </button>
        <button
          onClick={confirmRefresh}
          disabled={busy}
          style={{
            padding: '6px 10px',
            fontSize: '12px',
            borderRadius: '4px',
            border: '1px solid rgba(16,185,129,0.5)',
            background: 'rgba(16,185,129,0.12)',
            color: '#34d399',
            cursor: busy ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            whiteSpace: 'nowrap'
          }}
        >
          {busy ? <Loader2 size={12} className="spin" /> : <Check size={12} />}
          确认并刷新
        </button>
      </div>

      {/* 新建标签 */}
      <div style={{
        order: 2,
        background: T.section,
        border: `1px solid ${T.border}`,
        padding: '12px',
        borderRadius: '6px',
        marginBottom: '12px'
      }}>
        <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 600, color: T.text }}>新建标签</h4>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); createTag(); } }}
            placeholder="标签名称，如：奶油色"
            style={{ ...inputStyle, flex: 1 }}
          />
          <select
            value={newGroup}
            onChange={(e) => setNewGroup(e.target.value)}
            style={{ ...inputStyle, width: '130px' }}
          >
            {allGroupNames.map(g => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
          <button
            onClick={createTag}
            disabled={!newName.trim() || busy}
            style={{
              padding: '6px 12px',
              background: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: (!newName.trim() || busy) ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              whiteSpace: 'nowrap'
            }}
          >
            {busy ? <Loader2 size={13} className="spin" /> : <Plus size={13} />}
            新建
          </button>
        </div>
      </div>

      {/* 标签类型管理 */}
      <div style={{
        order: 1,
        background: T.section,
        border: `1px solid ${T.border}`,
        padding: '12px',
        borderRadius: '6px',
        marginBottom: '12px'
      }}>
        <h4 style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600, color: T.text }}>标签类型管理</h4>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <input
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); createGroup(); } }}
            placeholder="新建标签类型，如：品牌"
            style={{ ...inputStyle, flex: '1 1 180px' }}
          />
          <button onClick={createGroup} disabled={!newGroupName.trim() || busy} style={{
            padding: '6px 10px', background: '#10b981', color: '#fff', border: 'none',
            borderRadius: '4px', cursor: (!newGroupName.trim() || busy) ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px'
          }}>
            <FolderPlus size={13} /> 新建类型
          </button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
          <button
            onClick={() => setGroupManageOpen((open) => !open)}
            style={{
              padding: '4px 8px',
              border: `1px solid ${T.border}`,
              borderRadius: '4px',
              background: 'transparent',
              color: T.text2,
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            {groupManageOpen ? '收起管理' : '管理标签类型'}
          </button>
        </div>
        {groupManageOpen && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
          {groupCatalog.map((group, groupIndex) => (
            <div key={group.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 6px',
              border: `1px solid ${T.border}`, borderRadius: '12px', color: T.text2, fontSize: '12px'
            }}>
              {editingGroupName === group.id ? (
                <>
                  <input value={editingGroupValue} onChange={(e) => setEditingGroupValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveGroup(group); if (e.key === 'Escape') setEditingGroupName(null); }}
                    autoFocus style={{ ...inputStyle, width: '90px', padding: '2px 4px', fontSize: '12px' }} />
                  <button onClick={() => saveGroup(group)} disabled={busy} style={{ background: 'none', border: 0, color: '#34d399', cursor: 'pointer', padding: 0 }}><Check size={12} /></button>
                  <button onClick={() => setEditingGroupName(null)} style={{ background: 'none', border: 0, color: T.muted, cursor: 'pointer', padding: 0 }}><X size={12} /></button>
                </>
              ) : (
                <>
                  <span>{group.name} ({group.tag_count})</span>
                  <button onClick={() => { setEditingGroupName(group.id); setEditingGroupValue(group.name); }}
                    style={{ background: 'none', border: 0, color: T.text2, cursor: 'pointer', padding: 0 }} title="修改类型"><Pencil size={11} /></button>
                  <button onClick={() => deleteGroup(group)} disabled={busy}
                    style={{ background: 'none', border: 0, color: '#f87171', cursor: busy ? 'not-allowed' : 'pointer', padding: 0 }}
                    title="删除类型及其标签"><Trash2 size={11} /></button>
                  <button
                    onClick={() => moveGroup(group, 'up')}
                    disabled={busy || groupIndex === 0}
                    style={{ background: 'none', border: 0, color: T.text2, cursor: (busy || groupIndex === 0) ? 'not-allowed' : 'pointer', padding: 0, opacity: groupIndex === 0 ? 0.3 : 0.8 }}
                    title="类型上移"><ArrowUp size={11} /></button>
                  <button
                    onClick={() => moveGroup(group, 'down')}
                    disabled={busy || groupIndex === groupCatalog.length - 1}
                    style={{ background: 'none', border: 0, color: T.text2, cursor: (busy || groupIndex === groupCatalog.length - 1) ? 'not-allowed' : 'pointer', padding: 0, opacity: groupIndex === groupCatalog.length - 1 ? 0.3 : 0.8 }}
                    title="类型下移"><ArrowDown size={11} /></button>
                </>
              )}
            </div>
          ))}
        </div>
        )}
      </div>

      {/* 标签分组列表 */}
      <div style={{
        order: 3,
        border: `1px solid ${T.border}`,
        borderRadius: '6px',
        minHeight: '240px'
      }}>
        <div style={{
          padding: '10px 12px',
          borderBottom: `1px solid ${T.border}`,
          background: T.section,
          color: T.text,
          fontWeight: 600,
          fontSize: '13px'
        }}>
          标签字典
        </div>
        <div style={{ padding: '12px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '32px', color: T.muted }}>
              <Loader2 size={22} className="spin" />
            </div>
          ) : filteredGroups.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px', color: T.muted }}>
              {search ? '没有匹配的标签' : '暂无标签'}
            </div>
          ) : (
            filteredGroups.map(g => (
              <div key={g.name} style={{ marginBottom: '16px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: T.text,
                    padding: '2px 8px',
                    borderRadius: '10px',
                    background: T.hover
                  }}>
                    {g.name}
                  </span>
                  <span style={{ fontSize: '11px', color: T.muted }}>
                    {g.tags.length} 个
                  </span>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {g.tags.map(tag => {
                    const isEditing = editingId === tag.id;
                    return (
                      <div
                        key={tag.id}
                        style={{
                          display: 'inline-flex',
                          width: 'fit-content',
                          maxWidth: 'max-content',
                          flex: '0 0 auto',
                          alignSelf: 'flex-start',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '3px 6px 3px 10px',
                          fontSize: '12px',
                          borderRadius: '12px',
                          background: 'rgba(59,130,246,0.12)',
                          border: '1px solid rgba(59,130,246,0.3)',
                          color: '#60a5fa'
                        }}
                      >
                        {isEditing ? (
                          <>
                            <input
                              type="text"
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); saveEdit(); }
                                if (e.key === 'Escape') cancelEdit();
                              }}
                              autoFocus
                              style={{
                                width: '72px',
                                padding: '2px 4px',
                                fontSize: '12px',
                                border: `1px solid ${T.border}`,
                                borderRadius: '3px',
                                background: T.inputBg,
                                color: T.text
                              }}
                            />
                            <select
                              value={editingGroup}
                              onChange={(e) => setEditingGroup(e.target.value)}
                              style={{
                                padding: '2px 4px',
                                fontSize: '12px',
                                border: `1px solid ${T.border}`,
                                borderRadius: '3px',
                                background: T.inputBg,
                                color: T.text
                              }}
                            >
                              {allGroupNames.map(gn => (
                                <option key={gn} value={gn}>{gn}</option>
                              ))}
                            </select>
                            <button
                              onClick={saveEdit}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#34d399', display: 'flex', padding: 0 }}
                              title="保存"
                            >
                              <Check size={12} />
                            </button>
                            <button
                              onClick={cancelEdit}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.muted, display: 'flex', padding: 0 }}
                              title="取消"
                            >
                              <X size={12} />
                            </button>
                          </>
                        ) : (
                          <>
                            <span>{tag.name}</span>
                            {tag.usage_count > 0 && (
                              <span style={{ fontSize: '10px', opacity: 0.75 }}>{tag.usage_count}</span>
                            )}
                            <button
                              onClick={() => deleteTag(tag)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f87171', display: 'flex', padding: 0 }}
                              title="删除"
                            >
                              <Trash2 size={11} />
                            </button>
                            <button
                              onClick={() => startEdit(tag)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex', padding: 0, opacity: 0.7 }}
                              title="编辑"
                            >
                              <Pencil size={11} />
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default TagManager;
